/**
 * 合併冊的頁碼映射：絕對頁碼 ↔（成員序、成員內頁碼）。
 *
 * 閱讀器（標題顯示目前這一話、章節跳轉）、閱讀狀態（寫進哪個成員）、集合格（進度條）
 * 三處都要同一套換算，放在 shared 才不會各算各的。純函式，沒有狀態。
 */
export interface PageMap {
  /** 每個成員的起始絕對頁碼；頁數為 0 的成員與下一個成員共用起始頁，不佔任何絕對頁碼。 */
  readonly starts: readonly number[]
  readonly pageCount: number
}

export function buildPageMap(pageCounts: readonly number[]): PageMap {
  const starts: number[] = []
  let total = 0
  for (const count of pageCounts) {
    starts.push(total)
    total += Math.max(0, Math.trunc(count))
  }
  return { starts, pageCount: total }
}

/** 絕對頁碼落在哪個成員的第幾頁。越界拋 RangeError，與 PageSource 的契約一致。 */
export function locatePage(map: PageMap, page: number): { index: number; page: number } {
  if (!Number.isInteger(page) || page < 0 || page >= map.pageCount) throw new RangeError(`第 ${page + 1} 頁超出範圍（共 ${map.pageCount} 頁）`)
  // 找最後一個 start <= page 的成員；頁數為 0 的成員 start 與後一個相同，取後者才是真正持有該頁的成員。
  let low = 0, high = map.starts.length - 1
  while (low < high) {
    const middle = (low + high + 1) >> 1
    if (map.starts[middle] <= page) low = middle
    else high = middle - 1
  }
  return { index: low, page: page - map.starts[low] }
}

/** 成員序與成員內頁碼換算成絕對頁碼。成員序或頁碼超出該成員範圍時拋 RangeError。 */
export function absolutePage(map: PageMap, index: number, page: number): number {
  if (!Number.isInteger(index) || index < 0 || index >= map.starts.length) throw new RangeError(`沒有第 ${index + 1} 個成員`)
  const end = index + 1 < map.starts.length ? map.starts[index + 1] : map.pageCount
  const size = end - map.starts[index]
  if (!Number.isInteger(page) || page < 0 || page >= size) throw new RangeError(`第 ${index + 1} 個成員沒有第 ${page + 1} 頁`)
  return map.starts[index] + page
}
